import { useQueryClient } from 'react-query'
import { AppointmentType, AppointmentsApi, ErrorResponse } from '@citruscamps/citrus-client'
import { useToast } from '../../../hooks/useToast'
import translations from '../../../translations/en.json'
import { generateApiConfig } from '../../../utils/client-config'
import { generateBaseKey } from '../../../utils/key-generator'
import { useRequestHandler } from '../../../hooks/useRequestHandler'
import { AppointmentTypeSchema } from '../pages/AppointmentTypeDetails/schemas/AppointmentTypeSchema'

class LocalError implements ErrorResponse {
  constructor({ type, code, message, param, path, timestamp }: Partial<ErrorResponse>) {
    this.type = type || 'invalid_input_error'
    this.code = code || 400
    this.message = message || ''
    this.param = param || ''
    this.path = path || ''
    this.timestamp = timestamp || new Date().toJSON()
  }
  type: string
  code: number
  message: string
  param: string
  path: string
  timestamp: string
}

interface IProps {
  programId?: string
}

interface IUpdateAppointmentTypes {
  handlePublish: (appointmentType: AppointmentType) => Promise<void>
  handleUnpublish: (appointmentType: AppointmentType) => Promise<void>
  handleDelete: (appointmentType: AppointmentType) => Promise<void>
}

export const useUpdateAppointmentTypes = ({ programId }: IProps): IUpdateAppointmentTypes => {
  const { requestHandler } = useRequestHandler()
  const queryClient = useQueryClient()
  const { setToast } = useToast()

  const handlePublish = async (value: AppointmentType) => {
    const isValidAppointmentTypeDescription: boolean =
      AppointmentTypeSchema.getPartial('description').isValidSync(value)
    const isValidAppointmentTypeLogistics: boolean =
      AppointmentTypeSchema.getPartial('logistics').isValidSync(value)
    const isValidAppointmentTypeSchedule: boolean =
      AppointmentTypeSchema.getPartial('schedule_rules').isValidSync(value)
    const isValid: boolean =
      isValidAppointmentTypeDescription &&
      isValidAppointmentTypeLogistics &&
      isValidAppointmentTypeSchedule
    if (value?.status === 'draft' && isValid && programId) {
      const client = new AppointmentsApi(generateApiConfig())
      try {
        await requestHandler<AppointmentType>(
          () =>
            client.publishAppointmentType({ program_id: programId, appointment_type_id: value.id }),
          {
            toast: {
              success: (translations as any)['success.publish_appointment_type_message'],
              setToast,
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'appointment_type', program_id: programId }),
        )
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.publish_appointment_type_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    } else {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      let param: string = 'dashboard'
      if (!isValidAppointmentTypeDescription) {
        param = 'details'
      }
      if (!isValidAppointmentTypeLogistics) {
        param = 'options'
      }
      if (!isValidAppointmentTypeSchedule) {
        param = 'schedule'
      }
      throw new LocalError({
        message: (translations as any)['error.default_inputs_message'],
        param,
      })
    }
  }

  const handleUnpublish = async (value: AppointmentType) => {
    if (value?.status === 'live' && programId) {
      const client = new AppointmentsApi(generateApiConfig())
      try {
        await requestHandler<AppointmentType>(
          () =>
            client.unpublishAppointmentType({
              program_id: programId,
              appointment_type_id: value.id,
            }),
          {
            toast: {
              success: (translations as any)['success.unpublish_appointment_type_message'],
              setToast,
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'appointment_type', program_id: programId }),
        )
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.unpublish_appointmentType_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    } else {
      setToast?.({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
    }
  }

  const handleDelete = async (value: AppointmentType) => {
    if (programId) {
      const client = new AppointmentsApi(generateApiConfig())
      try {
        await requestHandler(
          () =>
            client.deleteAppointmentType({ program_id: programId, appointment_type_id: value.id }),
          {
            toast: {
              success: (translations as any)['success.delete_event_message'],
            },
          },
        )
        await queryClient.invalidateQueries(
          generateBaseKey({ type: 'appointment_type', program_id: programId }),
        )
      } catch (e: any) {
        setToast?.({
          header: (translations as any)['error.delete_event_toast_header'],
          message: e.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
      }
    }
  }

  return {
    handlePublish,
    handleUnpublish,
    handleDelete,
  }
}
